import { Fragment, MouseEvent } from 'react';
import { FieldValues } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { useToast } from '@givebutter/primitives/toast';
import { faBan, faCheck, faCircleNotch, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createEmailsRefer } from 'api/account';
import * as Form from 'components/form';
import { useCopy } from 'components/snippet';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useGrowSurf } from 'customHooks/useGrowSurf';
import buttermojisImage from 'images/buttermojis.svg';
import { twMerge } from 'tailwind-merge';

const referralLinkInputId = 'referral-link-input';

interface ShareCardValues {
    email: string;
}

const ShareCard = () => {
    const toast = useToast();
    const methods = Form.useGivebutterForm<ShareCardValues>();
    const { reset, handleSubmit, path, control } = methods;
    const { onClickCopy, didCopy } = useCopy();
    const { referralLink, accountId } = useCurrentAccount();
    useGrowSurf();

    const {
        mutate: submitRefer,
        isPending: isSubmitting,
        reset: resetMutation,
        isError,
        isSuccess,
    } = useMutation({
        mutationFn: (emails: string) => createEmailsRefer(accountId, [emails]),
        onError: () => {
            toast.error(
                <Fragment>
                    <span className="font-medium">Whoops.</span> Something went wrong. Check the
                    email and try again.
                </Fragment>,
                {
                    onExit: () => resetMutation(),
                },
            );
        },
        onSuccess: () => {
            reset({ email: '' });
            toast.success(
                <Fragment>
                    <span className="font-medium">Success!</span> Your invite has been sent!
                </Fragment>,
                {
                    onExit: () => resetMutation(),
                },
            );
        },
    });

    const handleCopyLinkClick = (e: MouseEvent<HTMLButtonElement>) => {
        onClickCopy(e, referralLink ?? '');
        selectLinkInputValue();
        toast.success('Referral Link copied to your clipboard!');
    };

    const onSubmit = ({ email }: FieldValues) => {
        submitRefer(email);
    };

    const getButtonLabel = () => {
        if (isSubmitting) {
            return <FontAwesomeIcon className={'flex h-5 w-5 animate-spin'} icon={faCircleNotch} />;
        }
        if (isError) {
            return <FontAwesomeIcon className={'flex h-5 w-5'} icon={faBan} />;
        }
        if (isSuccess) {
            return 'Invite Sent!';
        }

        return 'Send Invite';
    };

    const selectLinkInputValue = () => {
        const linkInput = document.getElementById('referral-link-input') as HTMLInputElement;
        linkInput?.select();
    };

    const getCopyButtonLabel = () => {
        return (
            <Fragment>
                {didCopy ? 'Copied' : 'Copy'}
                <FontAwesomeIcon className="ml-1" icon={didCopy ? faCheck : faCopy} />
            </Fragment>
        );
    };

    const submitButtonExtraClasses = () => {
        if (isError) {
            return 'bg-red-500';
        }
        if (isSuccess) {
            return 'bg-green-500';
        }

        return 'bg-blue-500';
    };

    if (!referralLink) return null;

    return (
        <Form.Provider {...methods}>
            <div
                className={`box-border flex min-h-[18.5rem] flex-col gap-4 rounded-lg bg-white bg-[length:auto_19rem] bg-[195%_0] bg-no-repeat p-6 shadow-md sm:flex-row sm:bg-[url('images/gradient-and-line.svg')]`}
            >
                <div className="flex flex-col gap-4 sm:max-w-[28rem]">
                    <div>
                        <h3 className="text-xl">Spread the butter and get $200 from us!</h3>
                        <p className="mt-1 text-gray-700" style={{ fontSize: '15px' }}>
                            Know someone in need of great fundraising tools? Refer them to
                            Givebutter! If they raise $10K in their first 6 months, we’ll send you
                            $200 as a thank you!
                        </p>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Form.Container>
                            <Form.Label name={path('email')}>Invite via email</Form.Label>
                            <div className="flex flex-row gap-3 align-top">
                                <Form.TextInputRoot
                                    control={control}
                                    name="email"
                                    rules={{ required: 'Email is required ' }}
                                >
                                    <Form.TextInputForm placeholder="Email address" type="email" />
                                </Form.TextInputRoot>
                                <Button
                                    variant="solid"
                                    type="submit"
                                    size="sm"
                                    className={twMerge('h-11 w-32', submitButtonExtraClasses())}
                                    disabled={isSubmitting || !referralLink}
                                >
                                    {getButtonLabel()}
                                </Button>
                            </div>
                        </Form.Container>
                    </form>

                    <Form.Container>
                        <Label htmlFor={referralLinkInputId}>Or, share this referral link</Label>
                        <div className="relative flex flex-row justify-between gap-4">
                            <input
                                id={referralLinkInputId}
                                className="box-border h-11 w-full cursor-text rounded-full border-[0.063rem] border-gray-300 bg-gray-50 p-4"
                                value={referralLink ?? ''}
                                onClick={selectLinkInputValue}
                                readOnly
                            />
                            <Button
                                variant="none"
                                onClick={handleCopyLinkClick}
                                color={didCopy ? 'success' : 'primary'}
                                className={
                                    'absolute right-[0.063rem] top-[0.063rem] h-[2.625rem] w-24 font-medium'
                                }
                            >
                                {getCopyButtonLabel()}
                            </Button>
                        </div>
                    </Form.Container>
                </div>
                <div className={`flex grow items-center justify-center`}>
                    <img
                        src={buttermojisImage}
                        alt="Buttermojis holding heart"
                        className="flex max-w-[14rem]"
                    />
                </div>
            </div>
        </Form.Provider>
    );
};

export { ShareCard };
